
import { APP_CONFIG } from "./config";
import { monitoring } from "./monitoring";
import { getLastAnalysisRawPayload, getLastHarmonyOverride } from "./analysisCache";
import { GLOBAL_SEVERITY_MATRIX } from "./severityEngine";
import { recomputeScoreWithNewLevel } from "./scoreService";
import { AnalysisResult } from "../types";
import { computeSHA256 } from "../utils/hashUtils";
import { stableStringify } from "../utils/jsonUtils";
import { ANALYSIS_INVARIANTS } from "../analysis/analysisInvariants";

/**
 * GSC TEST REPORT SERVICE v5.0 - INTEGRITY & AUDIT EDITION
 */

export async function buildCoachLevelTraceReport(analysis: AnalysisResult): Promise<any> {
    // SECURITY HARD GATE: Empêcher l'exportation non défendable
    if (!analysis.session.certified) {
        throw new Error("IMPOSSIBLE_D_EXPORTER : Cette session n'est pas certifiée (Calibration ou Intégrité manquante).");
    }

    const sessionId = analysis.audit_trail.scoring_ruleset_id.replace("GSC_SESSION_", "");
    const rawPayload = getLastAnalysisRawPayload(sessionId);
    
    if (!rawPayload) {
        throw new Error("DATA_LOST : Les données brutes ne sont plus en mémoire cache.");
    }

    const harmonyOverride = getLastHarmonyOverride(sessionId);
    
    const levelsTrace = [];
    for (let levelId = 1; levelId <= 8; levelId++) {
        const report = recomputeScoreWithNewLevel(rawPayload, levelId, harmonyOverride ?? undefined);
        const profile = GLOBAL_SEVERITY_MATRIX[levelId];
        
        levelsTrace.push({
            levelId,
            label: profile?.label || `Level ${levelId}`,
            score: report.score,
            metrics: report.metrics,
            reasons: report.reasons,
            weights: profile?.weights
        });
    }

    const reportBody = {
        meta: {
            report_type: "COACH_MULTI_LEVEL_TRACE",
            timestamp: new Date().toISOString(),
            sessionId,
            analysis_hash: analysis.audit_trail.analysis_hash,
            capture_hash: analysis.audit_trail.audio_hash,
            engine_version: analysis.audit_trail.engine_version,
            invariants: ANALYSIS_INVARIANTS
        },
        integrity_context: {
            is_certified: analysis.session.certified,
            calibration_status: analysis.session.truth_report.calibration_status,
            integrity_status: analysis.session.truth_report.integrity_status,
            trust_score: analysis.session.truth_report.confidence
        },
        levels: levelsTrace
    };

    // Génération de la signature locale de l'audit (Tamper-proof signature)
const bytes = new TextEncoder().encode(stableStringify(reportBody));
const local_signature = await computeSHA256(bytes.buffer);

    return {
        ...reportBody,
        local_signature
    };
}

export function downloadTestReport(report: any) {
  if (!report || report.error) return;
  const filename = `GSC_AUDIT_${report.meta?.analysis_hash?.slice(0,8) || Date.now()}.json`;
  const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export async function sendTelemetryReport(report: any) {
    try {
        monitoring.logDspSession(report);
        if (APP_CONFIG.MONITORING.DEBUG_MODE) {
            console.debug("[TELEMETRY] Industrial report shipped:", report.test_id);
        }
    } catch (e) {
        if (APP_CONFIG.MONITORING.DEBUG_MODE) {
            console.warn("[TELEMETRY] Internal shipping error:", e);
        }
    }
}

export function getDeepSnapshot(result: AnalysisResult, levelId: number) {
    return {
        score: result.session.score,
        metrics: result.session.metrics,
        level: levelId,
        audit: result.audit_trail
    };
}
